import DOMPurify from 'dompurify';
import { get } from 'lodash';
import { type CSSProperties } from 'react';
import { useMemo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ConsentGuard } from '@hivebrite/design-system/Embed';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { DivWithEmailStyles } from 'hb-react/shared/components/HtmlContent/DivWithEmailStyles';
import { DivWithWebStyles } from 'hb-react/shared/components/HtmlContent/DivWithWebStyles';
import { extractConsentGuardProps } from 'hb-react/shared/components/HtmlContent/utils/extractConsentGuardProps';
import { replaceIframesWithSpans } from 'hb-react/shared/components/HtmlContent/utils/replaceIframesWithSpans';

import ErrorBoundary from '../ErrorBoundary';

interface HtmlContentProps {
  config?: DOMPurify.Config;
  content: string;
  stylesConfig?: CSSProperties | 'email' | 'web';
}

const sanitizeHtml = (html: string, config: DOMPurify.Config | undefined) => {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName === 'IMG' && !node.hasAttribute('alt')) {
      node.setAttribute('alt', '');
    }
  });

  const sanitized = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target', 'alt'],
    FORBID_TAGS: ['form'],
    ...(config ?? {}),
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM: false,
  });

  DOMPurify.removeHook('afterSanitizeAttributes');

  return sanitized;
};

export const HtmlContent = ({
  content,
  config,
  stylesConfig,
}: HtmlContentProps) => {
  const [HtmlDiv, stylesConfigIsCustomCss] = useMemo(() => {
    if (stylesConfig === 'web') {
      return [DivWithWebStyles, false] as const;
    }
    if (stylesConfig === 'email') {
      return [DivWithEmailStyles, false] as const;
    }

    return [null, !!stylesConfig] as const;
  }, [stylesConfig]);

  const { currentNetwork } = useDefaultAppContexts();
  const containerRef = useRef<HTMLDivElement>(null);

  const [portals, setPortals] = useState<
    Array<{
      element: JSX.Element;
      key: string;
      target: Element;
    }>
  >([]);

  const displayThirdPartyConsent = useMemo(
    () =>
      // Do not use destructuring. In some cases BO.currentNetwork
      // may be undefined for awhile.
      get(
        currentNetwork,
        'features.cookies.config.display_third_party_consent',
      ),
    [currentNetwork],
  );

  const { dangerouslySetInnerHTML, replacements } = useMemo(() => {
    const sanitizedHtml = sanitizeHtml(content, config);

    if (!displayThirdPartyConsent) {
      return {
        dangerouslySetInnerHTML: { __html: sanitizedHtml },
        replacements: [],
      };
    }

    const { replacements, modifiedHtml } =
      replaceIframesWithSpans(sanitizedHtml);

    return {
      dangerouslySetInnerHTML: { __html: modifiedHtml },
      replacements,
    };
  }, [config, content, displayThirdPartyConsent]);

  // We use a combination of useEffect (which runs after the
  // render is committed to the screen) and a ref (to get a stable
  // reference to the container for imperative DOM manipulation) to ensure
  // we can reliably retrieve the portals targets even if they are rendered
  // within this component.
  useEffect(() => {
    // TS has a hard time understanding containerRef.current is not null
    // if we access it directly. So we need to assign it to a variable first.
    const container = containerRef.current;

    if (container) {
      const newPortals = replacements
        ?.map(({ iframe, spanId }) => {
          const span = container.querySelector(`#${spanId}`);

          if (!span) {
            return null;
          }

          const { hostName, providerName, aspectRatio } =
            extractConsentGuardProps(iframe);

          return {
            element: (
              <ConsentGuard
                hostName={hostName}
                providerName={providerName}
                aspectRatio={aspectRatio}
                isActive
              >
                {/* eslint-disable-next-line react/no-danger */}
                <div dangerouslySetInnerHTML={{ __html: iframe.outerHTML }} />
              </ConsentGuard>
            ),
            target: span,
            key: spanId,
          };
        })
        .filter(Boolean);

      setPortals(newPortals);
    }
  }, [replacements]);

  return (
    <ErrorBoundary>
      {HtmlDiv ? (
        <HtmlDiv
          ref={containerRef}
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        />
      ) : (
        <div
          style={
            stylesConfigIsCustomCss
              ? (stylesConfig as CSSProperties)
              : undefined
          }
          ref={containerRef}
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        />
      )}
      {portals.map(({ element, target, key }) =>
        createPortal(element, target, key),
      )}
    </ErrorBoundary>
  );
};

const withIframeConfig = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'scrolling'],
};

export const HtmlContentWithIframe = ({
  content,
  stylesConfig,
}: Omit<HtmlContentProps, 'config'>) => (
  <HtmlContent
    content={content}
    config={withIframeConfig}
    stylesConfig={stylesConfig}
  />
);

// Required for app/assets/javascripts/initializers/react/components/shared.js
export default HtmlContent;
